import VoxelStatistics from '../../enums/VoxelStatistics.js';
import { boxAverageReductionFactors, deriveBoxAverageGrid, } from '../../utilities/voxelGrid/index.js';
import { getBufferConfiguration } from '../../utilities/getBufferConfiguration.js';
import { gridLimitsOfProfile } from '../../utilities/gpuCapabilityProfiles.js';
import { isMutableSlab } from '../../cache/classes/VolumeTextureSet.js';
import { FULL_RESOLUTION_TEXTURE_SET } from '../../cache/classes/ImageVolume.js';
class FullExtentTextureStrategy {
    constructor({ name, volume, grid, statistic = VoxelStatistics.Average, }) {
        this.active = false;
        this.name = name;
        this.volume = volume;
        this.grid = grid;
        this.statistic = statistic;
    }
    isReady() {
        return Boolean(this.volume.getTextureSet(this.name));
    }
    bindings() {
        const member = this.volume.getTextureSet(this.name)?.members()[0];
        if (!member || isMutableSlab(member)) {
            return [];
        }
        return [
            {
                texture: member.texture,
                grid: member.grid,
                statistic: member.statistic,
                role: 'base',
            },
        ];
    }
    activate() {
        const set = this.volume.getTextureSet(this.name);
        if (this.active && this.claimedSet === set) {
            return;
        }
        this.claimedSet =
            set && this.volume.claimTextureSet(this.name) ? set : undefined;
        this.active = true;
    }
    deactivate() {
        if (!this.active) {
            return;
        }
        if (this.claimedSet &&
            this.claimedSet === this.volume.getTextureSet(this.name)) {
            this.volume.releaseTextureSet(this.name);
        }
        this.claimedSet = undefined;
        this.active = false;
    }
    update() {
        if (this.active) {
            this.activate();
        }
    }
}
function bytesPerVoxelOf(volume) {
    return getBufferConfiguration(volume.dataType, 1, { isVolumeBuffer: true })
        .numBytes;
}
export function reducedStrategyName(factors, statistic = VoxelStatistics.Average) {
    return `reduced-${factors.join('x')}/full-extent/${statistic}`;
}
export function provisionFullResolutionStrategy({ volume, profile, }) {
    const grid = volume.voxelGrid;
    const limits = gridLimitsOfProfile(profile, bytesPerVoxelOf(volume));
    const factors = boxAverageReductionFactors(grid.dimensions, limits);
    if (factors.some((factor) => factor > 1)) {
        return undefined;
    }
    const set = volume.provisionTextureSet({
        name: FULL_RESOLUTION_TEXTURE_SET,
        grids: [grid],
        coverage: 'full-extent',
        backstop: true,
    });
    if (!set) {
        return undefined;
    }
    return new FullExtentTextureStrategy({
        name: FULL_RESOLUTION_TEXTURE_SET,
        volume,
        grid,
    });
}
export function provisionReducedResolutionStrategy({ volume, profile, }) {
    const fullResolutionGrid = volume.voxelGrid;
    const limits = gridLimitsOfProfile(profile, bytesPerVoxelOf(volume));
    const factors = boxAverageReductionFactors(fullResolutionGrid.dimensions, limits);
    if (!factors.some((factor) => factor > 1)) {
        return undefined;
    }
    const statistic = volume.reductionStatistic ?? VoxelStatistics.Average;
    const grid = deriveBoxAverageGrid(fullResolutionGrid, { factors });
    const name = reducedStrategyName(factors, statistic);
    const set = volume.provisionTextureSet({
        name,
        grids: [grid],
        coverage: 'full-extent',
        statistic,
    });
    if (!set) {
        return undefined;
    }
    return new FullExtentTextureStrategy({ name, volume, grid, statistic });
}
export const defaultVolumeStrategyProvider = (context) => {
    const strategies = [];
    const full = provisionFullResolutionStrategy(context);
    if (full) {
        strategies.push(full);
        return strategies;
    }
    const reduced = provisionReducedResolutionStrategy(context);
    if (reduced) {
        strategies.push(reduced);
    }
    return strategies;
};
export const selectFirstReadyStrategy = ({ strategies, previous, }) => {
    if (previous?.isReady()) {
        return previous;
    }
    return strategies.find((strategy) => strategy.isReady());
};
